'use client';

import { LocationUtils } from '@ctct/rise-utils';
import { cn, selectTriggerVariants } from '@ctct/summit-core';
import { useTranslations } from 'next-intl';
import { useFormContext } from 'react-hook-form';

import { type FormFieldConfig } from '../../form-field';
import { FormField } from '../../form-field';
import { TextInput } from '../../inputs-core';
import { CountryCodeCombobox, CountryCodeDropdown } from '../country-dropdown';
import { StateAndProvinceInput } from '../StateAndProvinceInput';
import {
  AddressAutocompleteField,
  type AddressAutocompleteFieldProps,
} from './AddressAutocompleteField';

const BUSINESS_NAME_MAX_LENGTH = 80;

const COMBOBOX_STYLE = `${selectTriggerVariants({
  variant: 'md',
})} r:[&>span]:line-clamp-none r:[&>span]:truncate r:disabled:opacity-100`;

interface PhysicalAddressLabels {
  organizationName?: string;
  organizationNameDescription?: string;
  streetAddress: string;
  addressLine2?: string;
  country: string;
  stateProvince: string;
  cityTown: string;
  zipPostalCode: string;
}

interface PhysicalAddressValidationMessages {
  organizationNameRequired?: string;
  streetAddressRequired: string;
  countryRequired: string;
  stateProvinceRequired: string;
  cityTownRequired: string;
  zipPostalCodeRequired: string;
  zipPostalCodeInvalid?: string;
}

interface PhysicalAddressPlaceholders extends Partial<Record<PhysicalAddressFieldName, string>> {
  province?: string;
  region?: string;
  state?: string;
}

type PhysicalAddressFieldName =
  | 'organizationName'
  | 'addressLine1'
  | 'addressLine2'
  | 'country'
  | 'stateProvince'
  | 'cityTown'
  | 'zipPostalCode';

const DEFAULT_FIELD_ORDER: PhysicalAddressFieldName[] = [
  'organizationName',
  'country',
  'addressLine1',
  'addressLine2',
  'cityTown',
  'stateProvince',
  'zipPostalCode',
];

interface PhysicalAddressFieldsProps {
  labels: PhysicalAddressLabels;
  validationMessages: PhysicalAddressValidationMessages;
  formKeyPrefix?: string;
  fieldClassNames?: string | Partial<Record<PhysicalAddressFieldName, string>>;
  fieldOrder?: PhysicalAddressFieldName[];
  placeholders?: PhysicalAddressPlaceholders;
  dropdownContainer?: HTMLElement | null;
  hasOrganizationName?: boolean;
  hasAddressLine2?: boolean;
  isDisabled?: boolean;
  isRequired?: boolean;
  /** When omitted, follows `isRequired`. Set false to allow an empty sender name. */
  isOrganizationNameRequired?: boolean;
  shouldUseCombobox?: boolean;
  comboboxState?: {
    country?: { isOpen: boolean; onOpenChange: (open: boolean) => void };
    stateProvince?: { isOpen: boolean; onOpenChange: (open: boolean) => void };
  };
  addressAutocompleteProps?: Omit<
    AddressAutocompleteFieldProps,
    'formKey' | 'placeholder' | 'isDisabled' | 'registerOpts'
  >;
}

const getFormKey = (prefix: string | undefined, key: string) => (prefix ? `${prefix}.${key}` : key);

const getFieldClassName = (
  fieldClassNames: string | Partial<Record<PhysicalAddressFieldName, string>> | undefined,
  fieldName: PhysicalAddressFieldName
) => {
  if (!fieldClassNames) return undefined;
  if (typeof fieldClassNames === 'string') return fieldClassNames;
  return fieldClassNames[fieldName];
};

const PhysicalAddressFields = ({
  labels,
  validationMessages,
  formKeyPrefix,
  fieldClassNames,
  fieldOrder: fieldOrderProp = DEFAULT_FIELD_ORDER,
  placeholders,
  dropdownContainer,
  hasOrganizationName = false,
  hasAddressLine2 = true,
  isDisabled = false,
  isRequired = true,
  isOrganizationNameRequired,
  shouldUseCombobox = false,
  comboboxState,
  addressAutocompleteProps,
}: PhysicalAddressFieldsProps) => {
  const t = useTranslations('UiForm');
  const { watch, clearErrors, setValue, trigger, getValues } = useFormContext();
  const shouldRequireOrganizationName = isOrganizationNameRequired ?? isRequired;

  // Collapsed autocomplete: the street field is the only address input rendered, so a raw
  // typed string (no suggestion selected) leaves city/state/zip empty. Validate that a full
  // address was resolved, otherwise Continue silently no-ops with no field error.
  const cityKey = getFormKey(formKeyPrefix, 'city');
  const isCollapsedAutocompleteAddress =
    !!addressAutocompleteProps && !fieldOrderProp.includes('cityTown');

  const countryCodeKey = getFormKey(formKeyPrefix, 'countryCode');
  const countryCode = watch(countryCodeKey) as string | undefined;
  const normalizedCountryCode = countryCode?.toLowerCase();
  const stateCodeKey = getFormKey(formKeyPrefix, 'stateCode');
  const stateCode = watch(stateCodeKey) as string | undefined;
  const isUs = normalizedCountryCode === 'us';
  const isCanada = normalizedCountryCode === 'ca';
  const isStateSelect = isUs || isCanada;
  const stateVariant = isUs ? 'state' : isCanada ? 'province' : 'other';
  const stateProvincePlaceholder = isUs
    ? (placeholders?.state ?? placeholders?.stateProvince)
    : isCanada
      ? (placeholders?.province ?? placeholders?.stateProvince)
      : (placeholders?.region ?? placeholders?.stateProvince);

  const handleCountryChange = (value: string) => {
    clearErrors([
      countryCodeKey,
      getFormKey(formKeyPrefix, 'addressLine1'),
      getFormKey(formKeyPrefix, 'stateName'),
      getFormKey(formKeyPrefix, 'stateCode'),
      getFormKey(formKeyPrefix, 'city'),
      getFormKey(formKeyPrefix, 'postalCode'),
    ]);
    setValue(getFormKey(formKeyPrefix, 'stateCode'), '', { shouldDirty: false });
    setValue(getFormKey(formKeyPrefix, 'stateName'), '', { shouldDirty: false });
    comboboxState?.country?.onOpenChange(false);
  };

  const handleStateChange = (value: string) => {
    const locationMap = { state: LocationUtils.states, province: LocationUtils.provinces };
    const locations = locationMap[stateVariant as keyof typeof locationMap] ?? [];
    const location = locations.find((l) => l.abbreviation === value);
    if (location)
      setValue(getFormKey(formKeyPrefix, 'stateName'), location.name, { shouldDirty: false });

    // Dropdown selections don't fire blur events, so manually trigger validation
    trigger(getFormKey(formKeyPrefix, isStateSelect ? 'stateCode' : 'stateName'));
    comboboxState?.stateProvince?.onOpenChange(false);
  };

  // Build field config map — conditional fields are only added when enabled
  const fieldConfigMap: Partial<Record<PhysicalAddressFieldName, FormFieldConfig>> = {};

  if (hasOrganizationName) {
    fieldConfigMap.organizationName = {
      inputField: TextInput,
      label: labels.organizationName,
      description: labels.organizationNameDescription,
      className: getFieldClassName(fieldClassNames, 'organizationName'),
      inputOpts: {
        formKey: 'organizationName',
        autoComplete: 'organization',
        disabled: isDisabled,
        placeholder: placeholders?.organizationName,
        registerOpts: {
          required: {
            value: shouldRequireOrganizationName,
            message: validationMessages.organizationNameRequired ?? '',
          },
          maxLength: {
            value: BUSINESS_NAME_MAX_LENGTH,
            message: t('shared.organizationName.maxLength', { limit: BUSINESS_NAME_MAX_LENGTH }),
          },
        },
      },
    } satisfies FormFieldConfig<typeof TextInput>;
  }

  fieldConfigMap.addressLine1 = {
    inputField: addressAutocompleteProps ? AddressAutocompleteField : TextInput,
    label: labels.streetAddress,
    className: getFieldClassName(fieldClassNames, 'addressLine1'),
    inputOpts: {
      formKey: getFormKey(formKeyPrefix, 'addressLine1'),
      ...(!addressAutocompleteProps && { autoComplete: 'address-line1', disabled: isDisabled }),
      ...(addressAutocompleteProps && {
        isDisabled,
        dropdownContainer,
        ...addressAutocompleteProps,
      }),
      placeholder: placeholders?.addressLine1,
      registerOpts: {
        required: {
          value: isRequired,
          message: validationMessages.streetAddressRequired,
        },
        ...(isCollapsedAutocompleteAddress && {
          validate: (value: string) =>
            !value?.trim() || (getValues(cityKey) as string | undefined)?.trim()
              ? true
              : validationMessages.streetAddressRequired,
        }),
      },
    },
  };

  if (hasAddressLine2) {
    fieldConfigMap.addressLine2 = {
      inputField: TextInput,
      label: labels.addressLine2,
      className: getFieldClassName(fieldClassNames, 'addressLine2'),
      inputOpts: {
        formKey: getFormKey(formKeyPrefix, 'addressLine2'),
        autoComplete: 'address-line2',
        disabled: isDisabled,
        placeholder: placeholders?.addressLine2,
      },
    } satisfies FormFieldConfig<typeof TextInput>;
  }

  if (shouldUseCombobox) {
    fieldConfigMap.country = {
      inputField: CountryCodeCombobox,
      label: labels.country,
      className: getFieldClassName(fieldClassNames, 'country'),
      inputOpts: {
        formKey: countryCodeKey,
        disabled: isDisabled,
        buttonText: countryCode || '',
        triggerClasses: cn(COMBOBOX_STYLE, !countryCode?.length && 'r:text-neutral-600'),
        portalContainer: dropdownContainer,
        ...comboboxState?.country,
        registerOpts: {
          required: {
            value: isRequired,
            message: validationMessages.countryRequired,
          },
        },
        onChange: handleCountryChange,
      },
    };
  } else {
    fieldConfigMap.country = {
      inputField: CountryCodeDropdown,
      label: labels.country,
      className: getFieldClassName(fieldClassNames, 'country'),
      inputOpts: {
        formKey: countryCodeKey,
        disabled: isDisabled,
        container: dropdownContainer,
        registerOpts: {
          required: {
            value: isRequired,
            message: validationMessages.countryRequired,
          },
        },
        onValueChange: handleCountryChange,
      },
    } satisfies FormFieldConfig<typeof CountryCodeDropdown>;
  }

  fieldConfigMap.stateProvince = {
    inputField: StateAndProvinceInput,
    label: labels.stateProvince,
    className: getFieldClassName(fieldClassNames, 'stateProvince'),
    inputOpts: {
      formKey: getFormKey(formKeyPrefix, isStateSelect ? 'stateCode' : 'stateName'),
      autoComplete: 'address-level1',
      disabled: isDisabled,
      variant: stateVariant,
      shouldUseCombobox,
      placeholder: stateProvincePlaceholder,
      registerOpts: isStateSelect
        ? {
            required: {
              value: isRequired,
              message: validationMessages.stateProvinceRequired,
            },
          }
        : {},
      ...(shouldUseCombobox
        ? {
            portalContainer: dropdownContainer,
            buttonText: stateCode || '',
            triggerClasses: cn(COMBOBOX_STYLE, !stateCode?.length && 'r:text-neutral-600'),
            ...comboboxState?.stateProvince,
            onChange: handleStateChange,
          }
        : {
            container: dropdownContainer,
            onValueChange: handleStateChange,
          }),
    },
  };

  fieldConfigMap.cityTown = {
    inputField: TextInput,
    label: labels.cityTown,
    className: getFieldClassName(fieldClassNames, 'cityTown'),
    inputOpts: {
      formKey: getFormKey(formKeyPrefix, 'city'),
      autoComplete: 'address-level2',
      disabled: isDisabled,
      placeholder: placeholders?.cityTown,
      registerOpts: {
        required: {
          value: isRequired,
          message: validationMessages.cityTownRequired,
        },
      },
    },
  };

  const isPostalCodeRequired = isRequired && (isUs || isCanada);

  fieldConfigMap.zipPostalCode = {
    inputField: TextInput,
    label: labels.zipPostalCode,
    className: getFieldClassName(fieldClassNames, 'zipPostalCode'),
    inputOpts: {
      formKey: getFormKey(formKeyPrefix, 'postalCode'),
      autoComplete: 'postal-code',
      disabled: isDisabled,
      inputMode: isUs ? 'numeric' : 'text',
      placeholder: placeholders?.zipPostalCode,
      registerOpts: {
        required: {
          value: isPostalCodeRequired,
          message: validationMessages.zipPostalCodeRequired,
        },
        // Always set pattern (undefined for non-US) so re-registering after a country change
        // overwrites a previously-registered US ZIP rule instead of leaving it stale.
        pattern: isUs
          ? {
              value: /^\d{5}(-\d{4})?$/,
              message:
                validationMessages.zipPostalCodeInvalid ?? validationMessages.zipPostalCodeRequired,
            }
          : undefined,
      },
    },
  };

  // Assemble fields in specified order, skipping disabled/conditional fields
  const fields = fieldOrderProp
    .map((key) => fieldConfigMap[key])
    .filter((field): field is FormFieldConfig => !!field);

  return (
    <>
      {fields.map((field) => (
        <FormField key={field.inputOpts.formKey} fieldConfig={field} />
      ))}
    </>
  );
};

export {
  type PhysicalAddressFieldName,
  PhysicalAddressFields,
  type PhysicalAddressFieldsProps,
  type PhysicalAddressLabels,
  type PhysicalAddressPlaceholders,
  type PhysicalAddressValidationMessages,
};
